import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { useIsMobile } from '@/hooks/use-mobile';
import GalleryLightbox from '@/components/gallery/GalleryLightbox';
import GallerySlide from '@/components/gallery/GallerySlide';

function GalleryCarousel({ images = [], ariaLabel = 'Galleria immagini', enableZoom = false }) {
  const isMobile = useIsMobile();
  const [api, setApi] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showIndicator, setShowIndicator] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const closeLightbox = useCallback(() => setSelectedImage(null), []);
  const items = useMemo(() => images.filter((image) => image?.id && image?.src), [images]);

  useEffect(() => {
    if (!api) return;
    let indicatorTimer;
    const onSelect = () => {
      setSelectedIndex(api.selectedScrollSnap());
      setShowIndicator(true);
      window.clearTimeout(indicatorTimer);
      indicatorTimer = window.setTimeout(() => setShowIndicator(false), 2000);
    };
    onSelect();
    api.on('select', onSelect);
    api.on('reInit', onSelect);
    return () => {
      window.clearTimeout(indicatorTimer);
      api.off('select', onSelect);
      api.off('reInit', onSelect);
    };
  }, [api]);

  if (!items.length) return null;
  return <>
    <Carousel setApi={setApi} opts={isMobile ? { align: 'start', dragFree: false, loop: false, containScroll: 'trimSnaps' } : { align: 'start', dragFree: true, loop: false }} aria-label={ariaLabel} className={`mb-16 md:mb-24 ${isMobile ? 'left-1/2 w-screen -translate-x-1/2 touch-pan-y' : 'w-full'}`}>
      <CarouselContent className={isMobile ? 'ml-0' : '-ml-6'}>
        {items.map((item, index) => <CarouselItem key={item.id} className={isMobile ? 'basis-full pl-0' : 'basis-auto pl-6'} aria-label={`${index + 1} di ${items.length}`}>
          <GallerySlide item={item} index={index} total={items.length} isMobile={isMobile} isNear={Math.abs(index - selectedIndex) <= 1} onOpen={setSelectedImage} />
        </CarouselItem>)}
      </CarouselContent>
      <CarouselPrevious aria-label="Immagine precedente" className="left-3 hidden h-10 w-10 border-border bg-background/90 text-foreground shadow-none hover:bg-background md:flex" />
      <CarouselNext aria-label="Immagine successiva" className="right-3 hidden h-10 w-10 border-border bg-background/90 text-foreground shadow-none hover:bg-background md:flex" />
      {isMobile && items.length > 1 && <p aria-live="polite" className={`pointer-events-none absolute bottom-6 left-1/2 z-10 -translate-x-1/2 rounded-sm bg-white/70 px-3 py-1.5 font-sans text-[10px] font-normal tracking-[0.25em] text-[#2d2d2d] backdrop-blur-sm transition-opacity duration-500 ${showIndicator ? 'opacity-100' : 'opacity-0'}`}>
        {selectedIndex + 1} / {items.length}
      </p>}
    </Carousel>
    <GalleryLightbox image={selectedImage} enableZoom={enableZoom} onClose={closeLightbox} />
  </>;
}

export default memo(GalleryCarousel);